import { Divider, Grid, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BackButton from '../components/Buttons/BackButton';
import DetailsCard from '../components/DetailsCard/DetailsCard';
import DialogAction from '../components/Dialog/DialogAction';
import InfoBoxes from '../components/InfoBoxes/InfoBoxes';
import { VerticalStep, VerticalStepper } from '../components/Stepper';
import Theme from '../Themes/main.theme';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import FeedIcon from '@mui/icons-material/Feed';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { User_status, User_status_Dictionary } from '../components/dictionaries/UserStatus';
import HttpService from '../services/HttpService';
import CardStepContainer from '../components/CardStepsContainer/CardStepContainer';
import ActionButton from '../components/Buttons/ActionButton';
import InfoIcon from '@mui/icons-material/Info';
import { IntExt, PermissionsManageInternalUsers } from '../lib/permissions';
import UserService from '../services/UserService';
import { useUserDbInfo } from '../hooks/UserInfo';

export interface IUserDetails{
  id: number;
  afm: string;
  full_name: string;
  first_name: string;
  last_name: string
  email: string;
  auth: string;
  phone_number: string,
  profile_picture?: string,
  role: string;
  administrative_unit: string,
  is_enabled: boolean,
  can_be_deleted: boolean,
  completed_locations: number
  completed_professionals: number
  completed_applications: number
  in_progress_applications: number,
  administrative_unit_id: number,
  notes: [],
  roles: []
}

const UserDetails = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [data, setData] = useState<IUserDetails>({
    id: 0,
    afm: '',
    full_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    auth: '',
    role: '',
    administrative_unit: '',
    administrative_unit_id: 0,
    is_enabled: false,
    can_be_deleted: false,
    completed_locations: 0,
    completed_professionals: 0,
    completed_applications: 0,
    in_progress_applications: 0,
    notes: [],
    roles: []
  });
  
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const is_internal = UserService.hasRole([IntExt.Internal]);

  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { userDbInfo } = useUserDbInfo();

  const infoDetails = [
    {
      label: <Box display='flex' alignItems='center'>
        {t('Καταχωρίσεις Τοποθεσιών')} 
        <Tooltip
          PopperProps={{
            disablePortal: true
          }} title={t('Αιτήματα καταχώρισης/αλλαγής στοιχείων τοποθεσιών που του εγκρίθηκαν')}
          placement='top-start'>
          <InfoIcon sx={{color: Theme?.iconColor?.main, ml: 1, fontSize: '17px'}}/>
        </Tooltip>
      </Box> ,
      count: data.completed_locations
    },
    {
      label: <Box display='flex' alignItems='center'>
        {t('Καταχωρίσεις Επαγγελματιών')}
        <Tooltip
          PopperProps={{
            disablePortal: true
          }} title={t('Αιτήματα καταχώρισης/αλλαγής στοιχείων τοποθεσιών που του εγκρίθηκαν')}
          placement='top-start'>
          <InfoIcon sx={{color: Theme?.iconColor?.main, ml: 1, fontSize: '17px'}}/>
        </Tooltip>
      </Box> ,
      count: data.completed_professionals
    },
    ...(is_internal
      ? [{
        label: <Box display='flex' alignItems='center'>
          {t('Ολοκληρωμένα Αιτήματα')}
          <Tooltip
            PopperProps={{
              disablePortal: true
            }} title={t('Αιτήματα καταχώρισης που Διεκπεραίωσε')}
            placement='top-start'>
            <InfoIcon sx={{color: Theme?.iconColor?.main, ml: 1, fontSize: '17px'}}/>
          </Tooltip>
        </Box>,
        count: data.completed_applications
      }]
      : []),
    ...(is_internal
      ? [{
        label: <Box display='flex' alignItems='center'>
          {t('Αιτήματα Σε Επεξεργασία')}
          <Tooltip
            PopperProps={{
              disablePortal: true
            }} title={t('Αιτήματα καταχώρισης που είναι υπεύθυνος χειριστής τώρα')}
            placement='top-start'>
            <InfoIcon sx={{color: Theme?.iconColor?.main, ml: 1, fontSize: '17px'}}/>
          </Tooltip>
        </Box>,
        count: data.in_progress_applications
      }]
      : [])
  ];

  const contact_info_details = [
    {
      name: 'Email',
      value: data.email || '-'
    },
    {
      name: t('Τηλέφωνο'),
      value: data.phone_number || '-'
    }
  ];
  const user_details = [
    {
      name: t('Ρόλος'),
      value: data.roles.map((role: any) => role.key).filter(Boolean).length > 0
        ? data.roles.map((role: any) => role.key).filter(Boolean)
        : '-'
    },
    {
      name: t('ΑΦΜ'),
      value: data.afm || '-'
    },
    {
      name: t('Οργανική Μονάδα'),
      value: data.administrative_unit || '-'
    },
    {
      name: t('Κατάσταση'),
      value: data.is_enabled 
        ? User_status_Dictionary[User_status.Enabled]
        : User_status_Dictionary[User_status.Disabled]
    }
  ];
 
  const steps = [
    {
      label: t('Στοιχεία Επικοινωνίας'),
      content: <CardStepContainer isLoading={isLoading} detailsProps={{rows: contact_info_details, 
        removeLines: true}}/>,
      icon: <LocalPhoneIcon />
    },
    {
      label: t('Χαρακτηριστικά Στελέχους'),
      content: <CardStepContainer isLoading={isLoading} detailsProps={{rows: user_details, 
        removeLines: true}}/>,
      icon: <FeedIcon />
    },
  ];


  const handleSubmit = async () => {
    try {
      const response = await HttpService.getAxiosClient().delete(`${process.env.REACT_APP_BACKEND_URL}/users/${id}`);
      response.status === 200 && enqueueSnackbar(`${t('Ο χρήστης')} ${response.data.full_name} ${t('διαγράφτηκε επιτυχώς')}`, { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'), { variant: 'error' });
    }
  };

  const getData = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/users/${id}`);
      setData(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <>
      <Box mt={2} mb={2}>
        <BackButton onClick={() => navigate(-1)}/>
      </Box>
      <Typography variant='h2' color={Theme?.labelColor?.main}>{t('Προβολή Καρτέλας Εσωτερικού Χρήστη')}</Typography>
      <Divider sx={{my: 3, border: 1, color: Theme?.dividerColor?.main}}/>
      <Grid container spacing={{xs: 2, lg: 0}}>
        <Grid item xs={12}
          md={6}>
          <DetailsCard 
            actionButtons={
              <>
                <ActionButton 
                  disabled={!(UserService.hasRole([PermissionsManageInternalUsers
                    .PermissionsInternalUsersManageCreateAll])
                    || (UserService.hasRole([PermissionsManageInternalUsers
                      .PermissionsInternalUsersManageCreateMyOrg])
                       && userDbInfo?.administrative_unit_id === data.administrative_unit_id
                    ))}
                  title={t('Επεξεργασία Χρήστη')}
                  icon={<EditIcon/>}
                  onClick={() => navigate(`/admin/users/edit/${id}`)} />
                {UserService.hasRole([
                  PermissionsManageInternalUsers.PermissionInternalUsersDelete,
                ]) && <ActionButton 
                  title={t('Διαγραφή Χρήστη')} 
                  icon={<DeleteIcon color={
                    data.can_be_deleted
                      ? 'error'
                      : 'disabled'
                  }/>}
                  onClick={() => setOpen(true)}
                  disabled={!data?.can_be_deleted} 
                />
                }
              </>
            }
            icon={<PersonIcon fontSize='large'/>}
            title={data.full_name}
            isLoading={isLoading}>
            <VerticalStepper activeStep={0}>
              {steps.map(step => {
                return (
                  <VerticalStep
                    active={true}
                    key={step.label}
                    label={step.label}
                    icon={step.icon}>
                    {step.content}
                  </VerticalStep>
                );
              })}
            </VerticalStepper>
          </DetailsCard>
        </Grid>
        <Grid item xs={12}
          lg={6}>
          <Box display='flex' flexDirection='column'
            alignItems='center' ml={2}>
            <InfoBoxes infoDetails={infoDetails} title={t('Σύνολο Ενεργειών')}
              direction='column' isLoading={isLoading}/>
          </Box>
        </Grid>
      </Grid>
      <DialogAction open={open} mode='delete'
        handleClose={() => setOpen(false)}
        content={t('Είστε σίγουρος ότι θέλετε να διαγράψετε τον χρήστη;')}
        onAccept={() => {
          handleSubmit();
          setOpen(false);
        }}
      />
    </>
  );
};

export default UserDetails;